import { useState, useCallback, type ReactNode } from 'react'
import { HelpPanel } from './HelpPanel'
import { WelcomeModal } from './WelcomeModal'
import { HelpContext } from './helpContext'

const WELCOMED_KEY = 'debtos:welcomed:v1'

/**
 * Wraps the app and provides:
 *   - A first-visit welcome modal (one-time, dismiss persisted)
 *   - A slide-over help panel triggered via context's openHelp()
 *
 * Components anywhere in the tree can call useHelp() to open the panel.
 * `useHelp` lives in its own file so this module exports only the component
 * (required for React Fast Refresh to work cleanly).
 */
export function HelpProvider({ children }: { children: ReactNode }) {
  const [helpOpen, setHelpOpen] = useState(false)

  // Determine welcome visibility via lazy initializer so we don't paint a
  // closed modal then re-open it on the next tick — also avoids the
  // react-hooks/set-state-in-effect lint rule.
  const [welcomeOpen, setWelcomeOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(WELCOMED_KEY) === null
    } catch {
      // localStorage unavailable (private browsing) — skip welcome
      return false
    }
  })

  const dismissWelcome = useCallback(() => {
    setWelcomeOpen(false)
    try {
      localStorage.setItem(WELCOMED_KEY, new Date().toISOString())
    } catch {
      // ignore
    }
  }, [])

  const openHelp = useCallback(() => setHelpOpen(true), [])
  const closeHelp = useCallback(() => setHelpOpen(false), [])

  const handleOpenGuideFromWelcome = useCallback(() => {
    dismissWelcome()
    openHelp()
  }, [dismissWelcome, openHelp])

  return (
    <HelpContext.Provider value={{ openHelp }}>
      {children}
      {welcomeOpen && (
        <WelcomeModal onDismiss={dismissWelcome} onOpenGuide={handleOpenGuideFromWelcome} />
      )}
      <HelpPanel open={helpOpen} onClose={closeHelp} />
    </HelpContext.Provider>
  )
}
